<template>
  <v-container
    id="tags-list"
    tag="section"
  >
    <!-- Affichage des messages -->
    <v-row justify="center">
      <v-col cols="8">
        <timed-alert
          :display="msg.alert"
          :msg="msg.msg"
          :type="msg.type"
        />
      </v-col>
    </v-row>

    <!-- Modal de modification des labels -->
    <v-dialog
      v-model="editDialog"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          <!-- $t() est la fonction de traduction. On lui passe en paramètre le chemin dans le .json des locales -->
          <span class="headline secondary--text">{{ $t('TagsList.Edit.Title') }}<br><b>{{ editedItem.email }}</b></span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <!-- Formulaire -->
            <v-form
              ref="editForm"
              v-model="editIsValid"
            >
              <v-row>
                <v-col cols="12">
                  <v-combobox
                    v-model="editedItem.tags"
                    :items="existingTags"
                    :search-input.sync="editSearchTags"
                    :hide-no-data="!editSearchTags"
                    :label="$t('TagsList.Table.Headers.Tags')"
                    multiple
                    hide-selected
                    persistent-hint
                    chips
                    prepend-icon="mdi-label-multiple"
                  >
                    <!-- Affichage sous forme de chips -->
                    <template v-slot:selection="{ attrs, item, select, selected }">
                      <v-chip
                        v-if="item === Object(item) && item.text"
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        close-icon="mdi-close"
                        label
                        outlined
                        color="secondary"
                        @click="select"
                        @click:close="remove(item)"
                      >
                        <strong>{{ item.text }}</strong>
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="success darken-1"
            text
            :disabled="!editIsValid"
            @click="editItem()"
          >
            {{ $t('Btn.Modify') }}
          </v-btn>
          <v-btn
            color="error darken-1"
            text
            @click="editDialog = false"
          >
            {{ $t('Btn.Cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal d'explications des formats de fichier -->
    <v-dialog
      v-model="updateExampleDialog"
      max-width="500px"
    >
      <tags-update-example
        :tab="updateExampleTab"
        @set-example="handleSetExample"
      />
    </v-dialog>

    <!-- ******************** ***
    **** Section MAJ de masse ***
    **** ********************* -->
    <base-material-card
      color="primary"
      icon="mdi-earth"
      inline
      class="px-5 py-3"
    >
      <template v-slot:heading>
        <div class="display-2 font-weight-light">
          <v-icon left>
            mdi-table-edit
          </v-icon>
          {{ $t('TagsList.Update.Title') }}
        </div>
      </template>

      <!-- Téléchargement des données d'exemple -->
      <v-row>
        <v-col
          md="6"
          sm="12"
        >
          <v-card>
            <v-row
              justify="center"
              class="py-4 display-1"
            >
              {{ $t('TagsList.Update.Btn.DownloadExample') }}
            </v-row>
            <v-row justify="center">
              <v-btn
                color="secondary"
                text
                :href="csvExampleUrl"
                :download="generateExampleFilename + '.csv'"
                target="_blank"
                @click="handleDownloadCSV"
              >
                CSV
              </v-btn>
              <v-btn
                color="secondary"
                text
                @click="handleDownloadExampleXLSX"
              >
                XLSX
              </v-btn>
            </v-row>
          </v-card>
        </v-col>

        <!-- Téléchargement des données actuelles -->
        <v-col
          md="6"
          sm="12"
        >
          <v-card>
            <v-row
              justify="center"
              class="py-4 display-1"
            >
              {{ $t('TagsList.Update.Btn.DownloadFile') }}
            </v-row>
            <v-row justify="center">
              <v-btn
                color="secondary"
                text
                :href="csvUrl"
                :download="generateFilename + '.csv'"
                target="_blank"
                @click="handleDownloadCSV"
              >
                CSV
              </v-btn>
              <v-btn
                color="secondary"
                text
                @click="handleDownloadXLSX"
              >
                XLSX
              </v-btn>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-btn
          color="secondary"
          text
          small
          @click="updateExampleDialog = true"
        >
          {{ $t('TagsList.Update.Btn.FileFormat') }}
        </v-btn>
      </v-row>

      <v-divider />
      <v-divider />
      <v-divider />

      <!-- Téléchargement d'un fichier de labels -->
      <v-card>
        <v-row
          justify="center"
        >
          <v-col cols="4">
            <v-file-input
              v-model="importedFile"
              accept=".csv, .xlsx"
              :label="$t('TagsList.Update.FileInput.Label')"
            />
          </v-col>
        </v-row>
        <v-row
          justify="center"
        >
          <v-btn
            :disabled="!importedFile"
            color="secondary"
            text
            @click="handleImport"
          >
            {{ $t('TagsList.Update.Btn.UploadFile') }}
          </v-btn>
        </v-row>
      </v-card>
    </base-material-card>

    <!-- **************** ***
    ****      TABLE       ***
    **** **************** -->
    <base-material-card
      color="primary"
      icon="mdi-earth"
      inline
      class="px-5 py-3"
    >
      <template v-slot:heading>
        <div class="display-2 font-weight-light">
          <v-icon left>
            mdi-label-multiple
          </v-icon>
          {{ $t('TagsList.Table.Title') }}
        </div>
      </template>

      <!-- Champ de recherche dans la table -->
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        class="ml-auto"
        :label="$t('Tables.Search.Label')"
        hide-details
        single-line
        style="max-width: 250px;"
      />

      <v-divider class="mt-3" />

      <v-data-table
        :headers="headersT"
        :items="formattedItems"
        :search.sync="search"
        :sort-by="['email']"
        :sort-desc="[false]"
        :loading="isLoading"
        multi-sort
      >
        <!-- Affichage des tags sous forme de tuiles -->
        <template v-slot:item.tags="{ item }">
          <v-row
            v-if="item.tags[0].id !== null"
          >
            <v-chip
              v-for="tag in item.tags"
              :key="tag.id"
              class="ma-1"
              color="secondary"
              label
              outlined
            >
              {{ tag.text }}
            </v-chip>
          </v-row>
        </template>

        <!-- Définition de l'action d'édition -->
        <template v-slot:item.edit="{ item }">
          <v-row justify="center">
            <v-icon
              class="success--text"
              @click="prepareEditItem(item)"
            >
              mdi-pencil
            </v-icon>
          </v-row>
        </template>
      </v-data-table>
    </base-material-card>
  </v-container>
</template>

<script>
  /* Les composants et packages dont on a besoin */
  import TimedAlert from '../alerts/TimedAlert.vue'
  import TagsUpdateExample from '../components/subsections/TagsUpdateExample.vue'

  /* Rend le composant accessible aux autres composants */
  export default {
    /* Nom du composant */
    name: 'TagsList',

    /* Sous-composants utilisés par ce composant */
    components: {
      TimedAlert,
      TagsUpdateExample,
    },

    /* Variables internes
     * data doit être une fonction si ce composant est utilisé dans un autre composant */
    data: () => ({
      isLoading: true,

      // Gestion des fichiers
      csvUrl: undefined,
      csvExampleUrl: undefined,
      importedFile: undefined,
      updateExampleDialog: false,
      updateExampleTab: undefined,
      /*
          (?:         # begin non-capturing group
            \.        #   a dot
            (         #   begin capturing group (captures the actual extension)
              [^.]+   #     anything except a dot, multiple times
            )         #   end capturing group
          )?          # end non-capturing group, make it optional
          $           # anchor to the end of the string
      */
      fileExtensionRegex: /(?:\.([^.]+))?$/,

      // Variables pour le formulaire de modification
      existingTags: [],
      rules: {
        required: value => !!value || 'Champ obligatoire / Mandatory field',
      },
      editDialog: false,
      editIsValid: true,
      editedItem: {},
      editSearchTags: undefined,

      // Message de réussite ou d'échec des actions
      msg: {
        alert: false,
        msg: '',
        timeout: 5000,
        type: 'success',
      },

      // Variables relatives au tableau de données
      headers: [
        {
          text: 'TagsList.Table.Headers.User',
          value: 'email',
        },
        {
          text: 'TagsList.Table.Headers.Tags',
          value: 'tags',
        },
        {
          sortable: false,
          text: 'Tables.Headers.Edit',
          value: 'edit',
        },
      ],
      items: [],
      search: undefined,
    }),

    /* Variables internes dynamiques
     * (MAJ automatique quand les variables qu'elles utilisent sont mises à jour) */
    computed: {
      // Traduction des headers
      headersT: function () {
        return this.headers.map(header => {
          return {
            text: this.$t(header.text),
            value: header.value,
          }
        })
      },

      // Formattage des tags iso API
      formattedItems: function () {
        return this.items.map(item => {
          return {
            userId: item.userId,
            email: item.email,
            tags: item.tags.map(tag => {
              return {
                text: tag.tag,
                id: tag.id,
              }
            }),
          }
        })
      },

      // Création du nom de fichier des données actuelles
      // en fonction de la langue
      generateFilename: function () {
        return this.$t('TagsList.Update.DataFiles.Filename')
      },

      // Création du nom de fichier des données d'exemple
      // en fonction de la langue
      generateExampleFilename: function () {
        return this.$t('TagsList.Update.ExampleFiles.Filename')
      },

      // Données du fichier d'exemple en fonction de la langue
      exampleFileData: function () {
        const userStr = this.$t('TagsList.Update.ExampleFiles.User')
        const tagStr = this.$t('TagsList.Update.ExampleFiles.Tag')
        return [
          {
            email: userStr + '1',
            tags: [
              { tag: tagStr + '1' },
              { tag: tagStr + '2' },
              { tag: tagStr + '3' },
            ],
          },
          {
            email: userStr + '2',
            tags: [
              { tag: tagStr + '1' },
            ],
          },
          {
            email: userStr + '3',
            tags: [
              { tag: tagStr + '1' },
              { tag: tagStr + '2' },
            ],
          },
        ]
      },
    },

    /* Variables surveillées : traitement si modification de sa valeur */
    watch: {
      // Ajout d'un nouveau label
      'editedItem.tags': function (newVal, oldVal) {
        if (oldVal === undefined || newVal === undefined) return

        if (newVal.length === oldVal.length) return

        this.editedItem.tags = newVal.map(v => {
          if (typeof v === 'string') {
            v = {
              text: v,
            }
          }
          return v
        })
        this.editSearchTags = undefined
      },
    },

    /* Hook : fonction appelée juste après que l’instance ait été montée */
    mounted: function () {
      this.fetchUsersAndTags()
      this.fetchTags()
    },

    /* Fonctions internes */
    methods: {
      // Récuperation de tous les utilisateurs et de leurs labels
      fetchUsersAndTags: function () {
        this.isLoading = true
        this.$Auth.httpRequest('get', 'api/dashboard/selectUsersAndTags')
          .then(datas => {
            if (Array.isArray(datas.data) || datas.data.length > 0) {
              this.items = datas.data
              this.generateCSV()
              this.generateExampleCSV()
            }
          })
          .catch((/* error */) => {
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      },

      // Récuperation de tous les labels
      fetchTags: function () {
        this.isLoading = true
        this.$Auth.httpRequest('get', 'api/dashboard/selectTagsByCompanyId')
          .then(datas => {
            if (Array.isArray(datas.data) || datas.data.length > 0) {
              this.existingTags = datas.data.map(item => {
                return {
                  id: item.tagID,
                  text: item.tagName,
                }
              })
              this.isLoading = false
            }
          })
          .catch((/* error */) => {
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      },

      // Assure la bonne mise à jour de la fenêtre d'example
      handleSetExample: function (dialog, tab) {
        this.updateExampleDialog = dialog
        this.updateExampleTab = tab
      },

      // Actions à effectuer lors du téléchargement du .csv
      handleDownloadCSV: function () {
        this.updateExampleTab = 'csv'
        this.updateExampleDialog = true
      },

      // Actions à effectuer lors du téléchargement du .xlsx
      handleDownloadXLSX: function () {
        this.updateExampleTab = 'xlsx'
        this.updateExampleDialog = true
        this.generateXSLX()
      },

      // Actions à effectuer lors du téléchargement du .xlsx d'exemple
      handleDownloadExampleXLSX: function () {
        this.updateExampleTab = 'xlsx'
        this.updateExampleDialog = true
        this.$FilesConvert.exportTableToXLSX(
          this.exampleFileData,
          this.generateExampleFilename + '.xlsx',
        )
      },

      // Téléchargement du fichier de donnéees actuelles (format csv)
      generateCSV: function () {
        this.$FilesConvert.exportTableToCSV(this.items)
          .then(url => {
            this.csvUrl = url
          })
          .catch((/* error */) => {
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
      },

      // Téléchargement du fichier de donnéees actuelles (format csv)
      generateExampleCSV: function () {
        this.$FilesConvert.exportTableToCSV(this.exampleFileData)
          .then(url => {
            this.csvExampleUrl = url
          })
          .catch((/* error */) => {
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
      },

      // Téléchargement du fichier de donnéees actuelles (format xlsx)
      generateXSLX: function () {
        this.$FilesConvert.exportTableToXLSX(
          this.items,
          this.generateFilename + '.xlsx',
        )
      },

      // Importation du fichier de labels utilisateurs
      handleImport: function () {
        // Récupération de l'extension du fichier
        var extension = this.fileExtensionRegex.exec(this.importedFile.name)[1]
        switch (extension) {
          case 'csv':
            this.importCSV()
            break
          case 'xlsx':
            this.importXLSX()
            break
          default:
            break
        }
      },

      // Importation du fichier de labels utilisateurs (format csv)
      importCSV: function () {
        this.isLoading = true
        this.msg.alert = false
        this.$papa.parse(this.importedFile, {
          skipEmptyLines: true,

          // Fonction à appeler une fois le parsing fini, en succès ou en erreur
          complete: result => {
            this.importedFile = undefined

            // Gestion des erreurs, sinon envoi à l'API
            if (result.errors.length !== 0) {
              // Affichage de l'erreur
              this.setMsgError('TagsList.Update.Messages.InsertUsersTags.Error')
            } else {
              this.insertUsersTags(this.$FilesConvert.importCSVToTable(result.data))
            }
          },
        })
      },

      // Importation du fichier de labels utilisateurs (format xlsx)
      importXLSX: function () {
        const nbTagMax = 10
        this.$FilesConvert.readXLSX(this.importedFile, nbTagMax)
          .then(data => {
            this.importedFile = undefined
            this.insertUsersTags(this.$FilesConvert.importXLSXToTable(data, nbTagMax))
          })
          .catch((/* error */) => {
            // Affichage de l'erreur
            this.setMsgError('TagsList.Update.Messages.InsertUsersTags.Error')
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
      },

      // Envoie les labels utilisateurs à l'API pour insertion en base
      insertUsersTags: function (tags) {
        this.$Auth.httpRequest(
          'post',
          'api/dashboard/insertUsersTags',
          tags,
        )
          .then(datas => {
            // Affichage de la confirmation
            this.setMsgSuccess('TagsList.Update.Messages.InsertUsersTags.Success')
          })
          .catch((/* error */) => {
            // Affichage de l'erreur
            this.setMsgError('TagsList.Update.Messages.InsertUsersTags.Error')
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
          .finally(() => {
            this.isLoading = false
            this.fetchUsersAndTags()
            this.fetchTags()
          })
      },

      /* ********************************* **
      /** Fonctions relatives aux messages **
      /** ******************************** */
      /* Setter de message de réussite */
      setMsgSuccess: function (msg) {
        this.msg.msg = this.$t(msg)
        this.msg.alert = true
        this.msg.type = 'success'
        this.msg.icon = 'mdi-checkbox-marked-circle-outline'
      },

      /* Setter de message d'échec */
      setMsgError: function (msg) {
        this.msg.msg = this.$t(msg)
        this.msg.alert = true
        this.msg.type = 'error'
        this.msg.icon = 'mdi-error-outline'
      },

      /* *********************************** **
      /** Fonctions de modification de monde **
      /** ********************************** */
      prepareEditItem: function (item) {
        // Création du clone d'item
        this.editedItem = { ...item }
        // Création du clone du tableau
        // (afin d'éviter les effets de bord de la référence)
        this.editedItem.tags = [...item.tags]
        this.editDialog = true
      },

      remove: function (item) {
        this.editedItem.tags.splice(this.editedItem.tags.indexOf(item), 1)
        this.editedItem.tags = [...this.editedItem.tags]
      },

      // Formattage des données à insérer
      formattedTags: function () {
        const tags = []

        // Ajout de tous les tags inchangés
        this.items.forEach(item => {
          item.tags.forEach(t => {
            if (t.tag !== '' && item.email !== this.editedItem.email) {
              tags.push({
                email: item.email,
                tag: t.tag,
              })
            }
          })
        })

        // Ajout de la modification
        if (this.editedItem.tags) {
          this.editedItem.tags.forEach(t => {
            if (t.text !== '') {
              tags.push({
                email: this.editedItem.email,
                tag: t.text,
              })
            }
          })
        }

        return tags
      },

      // Envoie des données à l'API pour insertion en base
      editItem: function () {
        // On définie l'état en chargement
        this.isLoading = true
        this.editDialog = false
        this.msg.alert = false

        // Modification des tags
        this.$Auth.httpRequest(
          'post',
          'api/dashboard/insertUsersTags',
          this.formattedTags(),
        )
          .then(datas => {
            // console.log(datas)
            // Affichage de la confirmation
            this.setMsgSuccess('TagsList.Edit.Messages.Success')
          })
          .catch((/* error */) => {
            // Affichage de l'erreur
            this.setMsgError('TagsList.Edit.Messages.Error')
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
          .finally(() => {
            this.$refs.editForm.reset()
            this.isLoading = false
            this.fetchUsersAndTags()
            this.fetchTags()
          })
      },
    },
  }
</script>
